import { Tab, useTab, useTabsDataV2 } from 'hooks/useTabsDataV2'
import { PAGE_SIZE_1K } from 'modules/constants'
import { User } from 'modules/domain/user/types'
import { getUserTeamId } from 'modules/domain/user/utils'
import { endpoints } from 'modules/endpoints'
import { useTranslation } from 'react-i18next'
import { DashboardTab } from '../types'

const pageSize1k = {
  pageSize: PAGE_SIZE_1K,
}

const uniqFilters: Record<string, string[]> = {
  [DashboardTab.matches]: ['is_user_significant', 'match_margin_min', 'has_shipped_deals', 'sort'],
  [DashboardTab.calls]: ['is_user_significant_flag', 'company_size', 'has_shipped_deals_flag'],
  [DashboardTab.userWithoutBids]: ['last_bid_date_after', 'last_bid_date_before', 'user_responsibles'],
}

const extraExcludeFilterUserWithoutBids = 'assignee'

export const excludedFilters = {
  [DashboardTab.responses]: [
    ...uniqFilters[DashboardTab.matches],
    ...uniqFilters[DashboardTab.calls],
    ...uniqFilters[DashboardTab.userWithoutBids],
  ],
  [DashboardTab.matches]: [...uniqFilters[DashboardTab.calls], ...uniqFilters[DashboardTab.userWithoutBids]],
  [DashboardTab.calls]: [...uniqFilters[DashboardTab.matches], ...uniqFilters[DashboardTab.userWithoutBids]],
  [DashboardTab.userWithoutBids]: [
    ...uniqFilters[DashboardTab.matches],
    ...uniqFilters[DashboardTab.calls],
    extraExcludeFilterUserWithoutBids,
  ],
  [DashboardTab.newBids]: [
    ...uniqFilters[DashboardTab.matches],
    ...uniqFilters[DashboardTab.calls],
    ...uniqFilters[DashboardTab.userWithoutBids],
  ],
  [DashboardTab.newUsers]: [
    ...uniqFilters[DashboardTab.matches],
    ...uniqFilters[DashboardTab.calls],
    ...uniqFilters[DashboardTab.userWithoutBids],
  ],
  [DashboardTab.overdueBids]: [
    ...uniqFilters[DashboardTab.matches],
    ...uniqFilters[DashboardTab.calls],
    ...uniqFilters[DashboardTab.userWithoutBids],
  ],
}

export const useDashboardTabsConfig = (activeTab: string, currentUser?: User) => {
  const { t } = useTranslation('dashboard')

  const tabs: Record<string, Tab> = {}
  tabs[DashboardTab.responses] = useTab({
    label: t('responses'),
    endpoint: endpoints.dashboardResponses(),
    nonEditableParams: pageSize1k,
  })
  tabs[DashboardTab.matches] = useTab({
    label: t('matches'),
    endpoint: endpoints.dashboardMatches(),
    nonEditableParams: pageSize1k,
  })
  tabs[DashboardTab.calls] = useTab({
    label: t('callBack'),
    endpoint: endpoints.dashboardCalls(),
  })
  tabs[DashboardTab.userWithoutBids] = useTab({
    label: t('userWithoutBids'),
    endpoint: endpoints.dashboardUsersWithoutBids(),
  })
  tabs[DashboardTab.newUsers] = useTab({
    label: t('newUsers'),
    endpoint: endpoints.dashboardNewUsers(),
  })
  tabs[DashboardTab.newBids] = useTab({
    label: t('newBids'),
    endpoint: endpoints.dashboardNewBids(),
    nonEditableParams: pageSize1k,
  })

  tabs[DashboardTab.overdueBids] = useTab({
    label: t('expired'),
    endpoint: endpoints.dashboardOverdueBids(),
    nonEditableParams: pageSize1k,
  })

  const team = `${getUserTeamId(currentUser) || ''}`
  const commonInitial = {
    filter: team ? { team } : {},
  }

  const tabsData = useTabsDataV2({
    activeTab,
    tabs,
    initialCommonRequestParams: commonInitial,
    excludeFilters: excludedFilters,
  })

  return {
    ...tabsData,
    tabs,
  }
}
